import React, { useMemo, forwardRef } from 'react';
import { Card, Box, Typography } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { BASE_OPTIONS, EJE_X_BASE, EJE_Y_BASE, lineaAnotacion } from './chartBase';
import { COLOR, FUENTE, UMBRALES } from './tokens';
import InfoGrafico from './InfoGrafico';
import { MOCK_DEMANDA_FRANJA, MOCK_CAPACIDAD_FRANJA } from './mockDataEjemplos';
import { franjasConSobreturnos, ocupacionMediaFranjas, promedioPorDia } from './calculosReportes';

/**
 * Demanda por franja de 15 minutos — barras apiladas (cargado en recepción + agendó el bot), sin serie
 * "Total" (el total es la altura de la pila). Los dos colores son categóricos fijos (identidad de serie,
 * sin significado de alarma); las franjas donde se atendió por encima del cupo se marcan aparte con un
 * punto rojo sobre la barra — el rojo nunca es también el color de una serie.
 *
 * Las barras son el PROMEDIO DE TURNOS POR DÍA de cada franja (total del período ÷ días con turnos): la
 * misma unidad en que está expresada la capacidad configurada (turnos por franja y por día). Con un solo
 * día es el total de ese día. Antes se graficaba el acumulado del período contra la capacidad de un día y
 * casi toda franja salía "sobre capacidad".
 *
 * "Agendó el bot" / "cargado en recepción" es cómo se creó el turno (canal_origen), no cómo llegó la
 * persona: eso es la referencia, que usan "Estadísticas del bot" y el canal de origen.
 * @param {{data: Array<{horario: string, total: number, bot: number, manual: number, sobreturnos?: number}>,
 *   capacidad: number, dias?: number}} props
 */
const DemandaPorFranja = forwardRef(({ data, capacidad, dias = 1, compacto = false }, ref) => {
    const filas = useMemo(() => data || [], [data]);
    const porDia = (valor) => Math.round(promedioPorDia(valor, dias) * 10) / 10;

    // "Sobre capacidad" es lo que el propio sistema marca como sobreturno: un turno que excede el cupo
    // de su franja ese día. No se deduce de los totales del período.
    const conSobreturnos = useMemo(() => franjasConSobreturnos(filas), [filas]);
    const ocupacionMedia = useMemo(() => ocupacionMediaFranjas(filas, capacidad, dias), [filas, capacidad, dias]);
    const franjaPico = useMemo(
        () => filas.reduce((max, f) => (!max || f.total > max.total ? f : max), null),
        [filas]
    );
    // Ociosa = por debajo del piso de ocupación del sistema de umbrales (tokens.js: <70 % es ocioso).
    const franjasOciosas = useMemo(
        () => filas.filter((f) => capacidad && (promedioPorDia(f.total, dias) / capacidad) * 100 < UMBRALES.ocupacionPct.bueno[0]),
        [filas, capacidad, dias]
    );

    // El eje sale de los datos y de la capacidad: si saliera solo de la capacidad, una franja con más
    // demanda que ella quedaba cortada en el borde y todas parecían iguales.
    const maxBarra = Math.max(0, ...filas.map((f) => promedioPorDia(f.total, dias)));
    const yMax = Math.max(1, Math.ceil(Math.max((capacidad || 0) * 1.75, maxBarra * 1.15)));

    const chartData = {
        labels: filas.map((f) => f.horario),
        datasets: [
            {
                label: 'Cargado en recepción',
                data: filas.map((f) => porDia(f.manual)),
                backgroundColor: COLOR.azul,
                stack: 't',
                borderRadius: 3,
                barThickness: 10,
            },
            {
                label: 'Agendó el bot',
                data: filas.map((f) => porDia(f.bot)),
                backgroundColor: COLOR.azulClaro,
                stack: 't',
                borderRadius: 3,
                barThickness: 10,
            },
        ],
    };

    // Un punto rojo sobre cada franja con sobreturnos — separado de la identidad de serie.
    const annotationsCapacidad = {
        capacidad: lineaAnotacion(capacidad, COLOR.grisFrio, `capacidad ${capacidad}`, { dash: [4, 4] }),
    };
    conSobreturnos.forEach((f) => {
        annotationsCapacidad[`sobre-${f.horario}`] = {
            type: 'point',
            xValue: f.horario,
            yValue: promedioPorDia(f.total, dias),
            yAdjust: -10,
            radius: 4,
            backgroundColor: COLOR.rojo,
            borderColor: '#fff',
            borderWidth: 1.5,
        };
    });

    const options = {
        ...BASE_OPTIONS,
        plugins: {
            ...BASE_OPTIONS.plugins,
            annotation: { annotations: annotationsCapacidad },
        },
        scales: {
            x: {
                ...EJE_X_BASE,
                stacked: true,
                ticks: {
                    ...EJE_X_BASE.ticks,
                    autoSkip: false,
                    maxRotation: 0,
                    callback: (val, i) => (i % 4 === 0 ? filas[i]?.horario : ''),
                },
            },
            y: { ...EJE_Y_BASE, stacked: true, max: yMax, ticks: { ...EJE_Y_BASE.ticks, maxTicksLimit: 6 } },
        },
    };

    return (
        <Card variant="outlined" sx={{
            position: compacto ? 'static' : 'relative', border: compacto ? 'none' : '1px solid #e2e8f0',
            borderRadius: compacto ? 0 : '12px', boxShadow: 'none', padding: compacto ? 0 : '20px 22px',
        }}>
            {!compacto && (
                <>
                    <InfoGrafico
                        titulo="Demanda por franja"
                        queMuestra="Te dice cómo se reparte la demanda a lo largo del día, franja por franja de 15 minutos, y en cuáles se atendió por encima de la capacidad."
                        ejemploVisual={<DemandaPorFranja compacto data={MOCK_DEMANDA_FRANJA} capacidad={MOCK_CAPACIDAD_FRANJA} />}
                        comoLeerlo={[
                            'Cada barra es una franja de 15 minutos; su altura es el promedio de turnos por día en esa franja (el total del período dividido por los días con turnos). Con un solo día es el total de ese día.',
                            'Azul = turnos cargados en recepción. Celeste = turnos que agendó el chatbot. Es cómo se creó el turno, no cómo llegó la persona. Son series categóricas, ninguno de los dos colores es "alarma".',
                            'La línea gris punteada es la capacidad configurada: turnos por franja y por día, la misma unidad que las barras.',
                            'Un punto rojo arriba de una barra marca una franja donde hubo sobreturnos — turnos por encima de esa capacidad, que el sistema marca solo. Es la única marca de alarma del gráfico.',
                        ]}
                        analisis="Las franjas de 08:15 y 08:30 tienen sobreturnos (2 turnos cada una contra 1 de capacidad) mientras 09:15 queda vacía — es un problema de reparto, no de volumen total: en vez de sumar más capacidad todo el día, alcanzaría con correr uno de esos dos turnos de 08:15/08:30 hacia el hueco de 09:15."
                    />
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 16, fontWeight: 700, color: COLOR.negro }}>Demanda por franja</Typography>
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '14px' }}>
                        Turnos por franja de 15 minutos, promedio por día del período, contra la capacidad diaria configurada.
                    </Typography>
                </>
            )}
            <Box sx={{ height: compacto ? 170 : 260 }}>
                <Bar ref={ref} data={chartData} options={options} />
            </Box>
            {!compacto && (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '10px' }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: COLOR.rojo }} />
                        <Typography sx={{ fontFamily: FUENTE, fontSize: 11, color: COLOR.grisTexto }}>Franja con sobreturnos</Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: '12px', marginTop: '18px' }}>
                        <Resumen etiqueta="Franjas con sobreturnos" valor={conSobreturnos.length}
                            detalle={conSobreturnos.map((f) => f.horario).join(', ') || '—'} color={conSobreturnos.length ? COLOR.rojo : COLOR.negro} />
                        <Resumen etiqueta="Ocupación media" valor={ocupacionMedia != null ? `${ocupacionMedia.toFixed(0)}%` : '—'}
                            detalle="turnos por día ÷ capacidad" color={COLOR.negro} />
                        <Resumen etiqueta="Franja pico" valor={franjaPico ? `${franjaPico.horario} (${porDia(franjaPico.total)} por día)` : '—'} color={COLOR.negro} />
                        <Resumen etiqueta="Franjas ociosas" valor={franjasOciosas.length}
                            detalle={`ocupación < ${UMBRALES.ocupacionPct.bueno[0]}%`} color={COLOR.negro} />
                    </Box>
                </>
            )}
        </Card>
    );
});

const Resumen = ({ etiqueta, valor, detalle, color }) => (
    <Box sx={{ background: '#F8FAFC', border: '1px solid #F1F5F9', borderRadius: '8px', padding: '10px 12px' }}>
        <Typography sx={{ fontFamily: FUENTE, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: COLOR.grisTexto }}>
            {etiqueta}
        </Typography>
        <Typography sx={{ fontFamily: FUENTE, fontSize: 18, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>
            {valor}
        </Typography>
        {detalle && (
            <Typography sx={{ fontFamily: FUENTE, fontSize: 10.5, color: COLOR.grisTexto, marginTop: '2px' }} noWrap title={detalle}>
                {detalle}
            </Typography>
        )}
    </Box>
);

export default DemandaPorFranja;
